import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  UploadCloud, FileSpreadsheet, Download, ShieldAlert, Sparkles, Loader2, CheckCircle2,
  AlertTriangle, FileDown, Landmark, RefreshCw, Send,
} from "lucide-react";

const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const inr = (n) => (n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

const BUCKET_META = {
  MATCHED: { label: "Matched", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  MISSING_IN_BOOKS: { label: "Only in 2B", cls: "bg-red-50 text-red-700 border-red-200" },
  MISSING_IN_GST: { label: "Missing from 2B", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  VALUE_MISMATCH: { label: "Value mismatch", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  TAX_MISMATCH: { label: "Tax mismatch", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  GSTIN_MISMATCH: { label: "GSTIN mismatch", cls: "bg-red-50 text-red-700 border-red-200" },
  DUPLICATE: { label: "Duplicate", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function ImportCard({ title, kind, period, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (f) => {
    if (!f) return;
    if (!period) { toast.error("Enter a period first (e.g. 2026-05)"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f); fd.append("period", period);
    try {
      const { data } = await api.post(`/india-tax/gst/${kind}/import`, fd);
      if (data.possible_duplicate)
        toast.warning(`Possible duplicate — same file imported ${new Date(data.possible_duplicate.imported_at).toLocaleString()}`);
      else toast.success(`${title}: ${data.rows} rows imported`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const templ = kind === "2b" ? "2b" : "purchase";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`import-${kind}`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); up(e.dataTransfer.files?.[0]); }}>
      <div className="flex items-center gap-2 mb-2">
        <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
        <div className="text-sm font-semibold text-slate-800">{title}</div>
      </div>
      <input ref={ref} type="file" hidden accept=".xlsx,.xlsm,.csv"
        data-testid={`import-input-${kind}`}
        onChange={(e) => up(e.target.files?.[0])} />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => ref.current?.click()} disabled={busy}
          data-testid={`import-btn-${kind}`} className="bg-slate-900 hover:bg-slate-800 text-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UploadCloud className="h-4 w-4 mr-1" />} Import
        </Button>
        <a href={`${backend}/api/india-tax/gst/${templ}/template.xlsx`}
          className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid={`template-${kind}`}>
          <FileDown className="h-3.5 w-3.5" /> Template
        </a>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Drag & drop, or import. XLSX / CSV. No credentials needed.</p>
    </div>
  );
}

export default function ComplianceBridge() {
  const [period, setPeriod] = useState("2026-05");
  const [recon, setRecon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("EXCEPTIONS");
  const [modes, setModes] = useState(null);
  const [ai, setAi] = useState("");
  const [aiAns, setAiAns] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);

  useEffect(() => { api.get("/india-tax/connectors").then((r) => setModes(r.data)).catch(() => {}); }, []);

  const run = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const { data } = await api.get("/india-tax/gst/reconciliation", { params: { period } });
      setRecon(data);
    } catch { toast.error("Could not run reconciliation"); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { run(); }, [run]);

  const askAi = async () => {
    if (!ai.trim()) return;
    setAiBusy(true); setAiAns(null);
    try {
      const { data } = await api.post("/india-tax/gst/ai", { query: ai.trim(), period });
      setAiAns(data);
    } catch { toast.error("ONE AI unavailable"); }
    finally { setAiBusy(false); }
  };

  const trySubmit = async () => {
    setSubmitMsg(null);
    try {
      const { data } = await api.post("/india-tax/gst/submit", { query: "file", period });
      setSubmitMsg({ ok: true, text: data.note || "Ready for filing." });
    } catch (e) {
      setSubmitMsg({ ok: false, text: e?.response?.data?.detail || "Filing provider required." });
    }
  };

  const s = recon?.summary;
  const buckets = useMemo(() => {
    const m = {};
    (recon?.buckets || []).forEach((b) => { m[b.key] = b.count; });
    return m;
  }, [recon]);

  const rows = useMemo(() => {
    const all = recon?.rows || [];
    if (filter === "ALL") return all;
    if (filter === "EXCEPTIONS") return all.filter((r) => r.bucket !== "MATCHED");
    return all.filter((r) => r.bucket === filter);
  }, [recon, filter]);

  return (
    <div className="max-w-6xl mx-auto p-5 space-y-5" data-testid="compliance-bridge-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Landmark className="h-6 w-6 text-slate-800" />
          <div>
            <h1 className="text-xl font-bold text-slate-900">Tax & Compliance · Compliance Bridge</h1>
            <p className="text-sm text-slate-500">India GST / TDS / 26AS — no CompuTax credentials required.</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {(modes?.operating_modes || []).map((m) => (
            <Badge key={m.key} data-testid={`mode-${m.key}`}
              className={"text-[11px] border " + (m.status === "ACTIVE"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-50 text-amber-700 border-amber-200")}>
              {m.label}: {m.status === "ACTIVE" ? "Active" : "Setup"}
            </Badge>
          ))}
          {modes && (
            <Badge className="text-[11px] bg-slate-900 text-white border-slate-900" data-testid="no-cred-badge">
              NO-CREDENTIAL MODE: {modes.no_credential_mode}
            </Badge>
          )}
        </div>
      </div>

      {/* period + imports */}
      <div className="grid md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Compliance Period</div>
          <Input value={period} onChange={(e) => setPeriod(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()} placeholder="2026-05"
            data-testid="period-input" className="h-9" />
          <Button size="sm" variant="outline" onClick={run} data-testid="run-recon"
            className="mt-2 w-full">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Run reconciliation
          </Button>
        </div>
        <ImportCard title="GSTR-2B" kind="2b" period={period} onDone={run} />
        <ImportCard title="Purchase Register (books)" kind="purchase" period={period} onDone={run} />
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Export / share</div>
          <div className="space-y-1.5">
            <a href={`${backend}/api/india-tax/gst/export?period=${period}&kind=computax`}
              data-testid="export-computax"
              className="flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900">
              <Download className="h-4 w-4 text-slate-500" /> Export for CompuTax (XLSX)
            </a>
            <a href={`${backend}/api/india-tax/gst/export?period=${period}&kind=exceptions`}
              data-testid="export-exceptions"
              className="flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900">
              <Download className="h-4 w-4 text-slate-500" /> Exception report (XLSX)
            </a>
          </div>
        </div>
      </div>

      {/* dashboard cards (§5) */}
      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : s ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="gst-dashboard">
            {[
              ["Books invoices", s.books_rows, "text-slate-800"],
              ["Matched with 2B", s.matched, "text-emerald-600"],
              ["Missing from 2B", buckets.MISSING_IN_GST || 0, "text-amber-600"],
              ["Only in 2B", buckets.MISSING_IN_BOOKS || 0, "text-red-600"],
              ["Value mismatch", buckets.VALUE_MISMATCH || 0, "text-amber-600"],
              ["Tax mismatch", buckets.TAX_MISMATCH || 0, "text-amber-600"],
              ["Duplicates", buckets.DUPLICATE || 0, "text-slate-600"],
            ].map(([label, val, cls]) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                <div className={"text-2xl font-bold " + cls}>{val}</div>
                <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{label}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 flex items-center gap-2"
            data-testid="itc-banner">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-800"><strong>{inr(s.itc_at_risk)}</strong> of ITC needs review
              across <strong>{s.exceptions}</strong> exception(s).</span>
          </div>

          {/* filter chips + table (§21 exceptions-first) */}
          <div className="flex flex-wrap gap-1.5">
            {["EXCEPTIONS", "ALL", "MISSING_IN_GST", "MISSING_IN_BOOKS", "VALUE_MISMATCH", "TAX_MISMATCH", "GSTIN_MISMATCH"]
              .map((k) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
                  className={"text-xs px-2.5 py-1 rounded-full border " + (filter === k
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300")}>
                  {k === "EXCEPTIONS" ? "Exceptions only" : k === "ALL" ? "All"
                    : (BUCKET_META[k]?.label || k)}
                </button>
              ))}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <div className="col-span-2">Status</div>
              <div className="col-span-3">Supplier / GSTIN</div>
              <div className="col-span-2">Invoice</div>
              <div className="col-span-2 text-right">Books / 2B taxable</div>
              <div className="col-span-2 text-right">Books / 2B tax</div>
              <div className="col-span-1 text-right">ITC</div>
            </div>
            {rows.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-400" data-testid="recon-empty">
                {recon?.has_data ? "No rows in this view." : "Import a GSTR-2B and Purchase Register to begin."}
              </div>
            ) : rows.slice(0, 500).map((r, i) => {
              const meta = BUCKET_META[r.bucket] || { label: r.bucket, cls: "bg-slate-100 text-slate-600 border-slate-200" };
              return (
                <div key={r.invoice_key + i} data-testid={`recon-row-${i}`}
                  className="grid grid-cols-12 gap-2 px-4 py-2 items-center border-b border-slate-50 text-sm">
                  <div className="col-span-2"><Badge className={"text-[10px] border " + meta.cls}>{meta.label}</Badge></div>
                  <div className="col-span-3 min-w-0">
                    <div className="truncate text-slate-800">{r.trade_name || "—"}</div>
                    <div className="text-[11px] text-slate-400 truncate">{r.gstin || "—"}</div>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <div className="truncate text-slate-700">{r.invoice_no}</div>
                    <div className="text-[11px] text-slate-400">{r.invoice_date}</div>
                  </div>
                  <div className="col-span-2 text-right text-[12px] text-slate-600">
                    {inr(r.books_taxable)} <span className="text-slate-300">/</span> {inr(r.gst_taxable)}
                  </div>
                  <div className="col-span-2 text-right text-[12px] text-slate-600">
                    {inr(r.books_tax)} <span className="text-slate-300">/</span> {inr(r.gst_tax)}
                  </div>
                  <div className="col-span-1 text-right text-[12px] text-slate-700">{inr(r.itc)}</div>
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {/* ONE AI + filing protection */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4" data-testid="gst-ai">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-violet-600" />
            <div className="text-sm font-semibold text-slate-800">Ask ONE AI</div>
          </div>
          <div className="flex gap-2">
            <Input value={ai} onChange={(e) => setAi(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && askAi()} data-testid="ai-input"
              placeholder="Which invoices are missing from 2B?" className="h-9" />
            <Button size="sm" onClick={askAi} disabled={aiBusy} data-testid="ai-ask"
              className="bg-violet-600 hover:bg-violet-700 text-white">
              {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ask"}
            </Button>
          </div>
          {aiAns && (
            <div className="mt-3 text-sm text-slate-700 bg-white rounded-lg border border-violet-100 p-3" data-testid="ai-answer">
              {aiAns.answer}
              {(aiAns.rows || []).length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[12px] text-slate-500">
                  {aiAns.rows.slice(0, 6).map((r, i) => (
                    <li key={i}>• {r.invoice_no} — {r.trade_name || r.gstin} ({BUCKET_META[r.bucket]?.label || r.bucket})</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="filing-protection">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="h-4 w-4 text-red-500" />
            <div className="text-sm font-semibold text-slate-800">Filing (protected)</div>
          </div>
          <p className="text-[12px] text-slate-500 mb-3">
            ONE prepares & reconciles. Actual statutory filing needs an authorised GSP/ASP or CompuTax
            with DSC/eSign/OTP — ONE never bypasses this.
          </p>
          <Button size="sm" variant="outline" onClick={trySubmit} data-testid="try-submit">
            <Send className="h-3.5 w-3.5 mr-1" /> Attempt submission
          </Button>
          {submitMsg && (
            <div data-testid="submit-result"
              className={"mt-3 text-[12px] rounded-lg border p-2.5 flex items-start gap-2 " + (submitMsg.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-red-50 border-red-200 text-red-800")}>
              {submitMsg.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                : <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />}
              <span>{submitMsg.text}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
